// src/pages/Step6.jsx
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ProgressBar from '../components/ProgressBar';
import { postCotation, getMedicalInfo } from '../services/assuranceService';

// UUID pour le type SOLO
const SOLO_TYPE_UUID = '019beade-d8aa-71da-85e4-afa966b97457';

export default function Step6() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [medicalQuestions, setMedicalQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [questionResponses, setQuestionResponses] = useState({});

  // Récupérer la configuration familiale du localStorage avec state React
  const [hasSpouse, setHasSpouse] = useState(false);
  const [numberOfChildren, setNumberOfChildren] = useState(0);

  // Photos des cartes d'assurance antérieure (par personne)
  const [previousInsurance, setPreviousInsurance] = useState({
    principal: { hadInsurance: null, cardPhoto: null },
    spouse: { hadInsurance: null, cardPhoto: null },
    children: []
  });

  // Mettre à jour les valeurs depuis le localStorage au montage du composant
  useEffect(() => {
    const storedAssuredType = localStorage.getItem('assuredType');
    const storedHasSpouse = localStorage.getItem('hasSpouse') === 'true';
    const storedNumberOfChildren = parseInt(localStorage.getItem('numberOfChildren')) || 0;

    // Si l'offre est SOLO, on ignore le conjoint et les enfants
    const isSoloType = storedAssuredType === SOLO_TYPE_UUID;

    if (isSoloType) {
      setHasSpouse(false);
      setNumberOfChildren(0);
      setPreviousInsurance(prev => ({ ...prev, children: [] }));
    } else {
      setHasSpouse(storedHasSpouse);
      setNumberOfChildren(storedNumberOfChildren);
      setPreviousInsurance(prev => ({
        ...prev,
        children: Array.from({ length: storedNumberOfChildren }, () => ({ hadInsurance: null, cardPhoto: null }))
      }));
    }

    // Charger les questions médicales depuis l'API
    const fetchMedicalQuestions = async () => {
      try {
        const result = await getMedicalInfo();
        if (result && result.data) {
          setMedicalQuestions(result.data);
        }
      } catch (err) {
        console.error('Erreur lors du chargement des questions médicales:', err);
      } finally {
        setQuestionsLoading(false);
      }
    };

    fetchMedicalQuestions();
  }, []);

  // Gestion des photos cartes assurance antérieure
  const handleInsuranceCardPhoto = (personKey, file, childIndex = null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (childIndex !== null) {
        setPreviousInsurance(prev => {
          const newChildren = [...prev.children];
          newChildren[childIndex] = { ...newChildren[childIndex], cardPhoto: reader.result };
          return { ...prev, children: newChildren };
        });
      } else {
        setPreviousInsurance(prev => ({
          ...prev,
          [personKey]: { ...prev[personKey], cardPhoto: reader.result }
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleHadInsurance = (personKey, value, childIndex = null) => {
    if (childIndex !== null) {
      setPreviousInsurance(prev => {
        const newChildren = [...prev.children];
        newChildren[childIndex] = {
          ...newChildren[childIndex],
          hadInsurance: value,
          cardPhoto: value === 'no' ? null : newChildren[childIndex].cardPhoto
        };
        return { ...prev, children: newChildren };
      });
    } else {
      setPreviousInsurance(prev => ({
        ...prev,
        [personKey]: { hadInsurance: value, cardPhoto: value === 'no' ? null : prev[personKey].cardPhoto }
      }));
    }
  };

  // Fonction pour extraire les conditions médicales sélectionnées à partir des données du formulaire
  const extractMedicalConditions = (formData, prefix) => {
    const conditions = [];

    medicalQuestions.forEach(question => {
      const key = `${prefix}[${question.key}]`;
      const value = formData[key];

      if (value) {
        if (Array.isArray(value)) {
          conditions.push(...value);
        } else if (typeof value === 'string' && value.trim() !== '') {
          conditions.push(value);
        }
      }
    });

    return conditions;
  };

  // Sauvegarder les données du formulaire et calculer la cotation
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const formData = new FormData(e.target);
    const data = {};
    for (let [key, value] of formData.entries()) {
      if (data[key]) {
        if (Array.isArray(data[key])) {
          data[key].push(value);
        } else {
          data[key] = [data[key], value];
        }
      } else {
        data[key] = value;
      }
    }

    localStorage.setItem('step6FormData', JSON.stringify(data));
    localStorage.setItem('previousInsurance', JSON.stringify(previousInsurance));

    // Récupérer les données des étapes précédentes
    const selectedPackage = localStorage.getItem('selectedPackage');
    const assuredType = localStorage.getItem('assuredType');
    const activityClass = localStorage.getItem('activityClass');
    const isPrincipal = localStorage.getItem('isPrincipal');
    const subscriberData = JSON.parse(localStorage.getItem('subscriberData') || '{}');
    const principalData = JSON.parse(localStorage.getItem('principalData') || '{}');
    const spouse = JSON.parse(localStorage.getItem('spouse') || '{}');
    const children = JSON.parse(localStorage.getItem('children') || '[]');

    // Récupérer la date de naissance du principal selon si le souscripteur est le principal ou non
    const principalDob = isPrincipal === 'yes'
      ? subscriberData.dateNaissance
      : principalData.dateNaissance;

    // Vérifier que les données essentielles sont présentes
    if (!selectedPackage) {
      setError('Erreur: Package non sélectionné');
      setIsLoading(false);
      return;
    }
    if (!assuredType) {
      setError('Erreur: Type d\'assurance non sélectionné');
      setIsLoading(false);
      return;
    }
    if (!activityClass) {
      setError('Erreur: Classe d\'activité non sélectionné');
      setIsLoading(false);
      return;
    }
    if (!principalDob || principalDob.trim() === '') {
      setError('Erreur: Date de naissance du principal manquante ou invalide');
      setIsLoading(false);
      return;
    }

    // Vérifier le format de la date (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(principalDob)) {
      setError('Erreur: Format de date invalide. Utilisez le format YYYY-MM-DD');
      setIsLoading(false);
      return;
    }

    // Extraire les conditions médicales pour l'assuré principal
    const principalMedicalConditions = extractMedicalConditions(data, 'principal_insured');

    // Construire l'objet médical au format attendu par l'API
    const medical = {
      principal_insured: principalMedicalConditions
    };

    // Ajouter les conditions médicales du conjoint si présent
    if (hasSpouse) {
      const spouseMedicalConditions = extractMedicalConditions(data, 'spouse');
      medical.spouse = spouseMedicalConditions;
    }

    // Ajouter les conditions médicales des enfants si présents
    if (numberOfChildren > 0) {
      medical.children = {};
      for (let i = 1; i <= numberOfChildren; i++) {
        const childMedicalConditions = extractMedicalConditions(data, `child_${i}`);
        medical.children[i.toString()] = childMedicalConditions;
      }
    }

    // Construire le payload au format attendu par l'API
    const cotationData = {
      package: selectedPackage,
      type: assuredType,
      activity_class: activityClass,
      principal_insured_dob: principalDob,
      medical: medical
    };

    // Ajouter la date de naissance du conjoint si présent
    if (hasSpouse && spouse.dob) {
      cotationData.spouse_dob = spouse.dob;
    }

    // Ajouter les enfants avec leurs dates de naissance
    if (numberOfChildren > 0 && children.length > 0) {
      cotationData.children = children
        .filter(child => child && child.dob)
        .map(child => ({ dob: child.dob }));
    }

    try {
      const result = await postCotation(cotationData);

      // Vérifier si la réponse est valide
      if (!result || (typeof result !== 'object' && typeof result !== 'number')) {
        setError('Erreur: Réponse API invalide');
        setIsLoading(false);
        return;
      }

      localStorage.setItem('cotationResult', JSON.stringify(result));

      // Naviguer vers la page de déclaration après succès
      navigate('/medical-declaration');
    } catch (err) {
      setError('Erreur lors du calcul de la cotisation: ' + (err.response?.data?.message || err.message));
      setIsLoading(false);
    }
  };

  // Fonction pour rendre une question en fonction de son type (dans une cellule du tableau)
  const renderQuestion = (question, prefix) => {
    const key = `${prefix}[${question.key}]`;
    const responseKey = `${prefix}_${question.key}`;
    const response = questionResponses[responseKey];
    const hasMoreInfos = question.more_infos && response === 'true';

    const handleChange = (value) => {
      setQuestionResponses(prev => ({
        ...prev,
        [responseKey]: value
      }));
    };

    switch (question.type) {
      case 'text':
      case 'number':
        return (
          <div key={question.key} className="space-y-2">
            <div>
              <input
                type={question.type === 'number' ? 'number' : 'text'}
                name={key}
                defaultValue={question.type === 'number' ? 0 : 'Aucun'}
                placeholder={question.name}
                className="w-full border border-gray-300 rounded px-2 py-1" required
              />
            </div>
          </div>
        );

      case 'boolean':
        return (
          <div key={question.key} className="space-y-2">
            <div className="flex gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={key}
                  value="true"
                  onChange={(e) => handleChange(e.target.value)}
                  checked={response === 'true'} required
                />
                Oui
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name={key}
                  value="false"
                  onChange={(e) => handleChange(e.target.value)}
                  checked={response === 'false'} required
                />
                Non
              </label>
            </div>
            {hasMoreInfos && question.more_infos.field && (
              <div>
                <label className="block text-sm font-medium mb-1">{question.more_infos.field.label}</label>
                <input
                  type="text"
                  name={`${prefix}[${question.key}_infos]`}
                  placeholder={question.more_infos.field.label}
                  className="w-full border border-gray-300 rounded px-2 py-1" required
                />
              </div>
            )}
          </div>
        );

      case 'multiple':
        return (
          <div key={question.key} className="space-y-2">
            {question.options && question.options.map(option => (
              <label key={option} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name={key}
                  value={option}
                />
                {option}
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  if (questionsLoading) {
    return (
      <div className="py-10 lg:py-16 bg-[#e4eef9] min-h-screen flex items-center justify-center">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white p-10 rounded-xl shadow-md">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-lg font-medium text-gray-700">Chargement du questionnaire médical...</p>
              <p className="text-sm text-gray-500">Veuillez patienter un moment</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Construire la liste des personnes pour le bloc assurance antérieure
  const persons = [
    { key: 'principal_insured', label: 'Assuré Principal', prevKey: 'principal', childIndex: null }
  ];
  if (hasSpouse) {
    persons.push({ key: 'spouse', label: 'Conjoint(e)', prevKey: 'spouse', childIndex: null });
  }
  for (let i = 0; i < numberOfChildren; i++) {
    persons.push({ key: `child_${i + 1}`, label: `${i + 1}ème Enfant`, prevKey: 'children', childIndex: i });
  }

  return (
    <div className="subscribe py-10 lg:py-16 bg-[#e4eef9]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h1 className="text-center text-4xl font-bold">Formulaire de souscription</h1>
        <div className="mt-10">
          <ProgressBar currentStep={6} />
          <div className="mt-8 bg-white p-6 lg:p-10 rounded-xl shadow-sm">
            <h2 className="text-xl font-bold text-secondary mb-6">6. Questionnaire médical</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              {/* Questionnaire médical en tableau (design original) */}
              <div className="mt-6">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-primary text-white">
                        <th className="p-2 text-left font-bold">Questions</th>
                        <th className="p-2 text-left font-bold">Assuré Principal</th>
                        {hasSpouse && <th className="p-2 text-left font-bold">Conjoint(e)</th>}
                        {[...Array(numberOfChildren)].map((_, i) => (
                          <th key={i} className="p-2 text-left font-bold">{i + 1}ème Enfant</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {medicalQuestions.map((question, rowIndex) => (
                        <tr key={question.key} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-2 border font-medium">{question.name}</td>
                          <td className="p-2 border">
                            {renderQuestion(question, 'principal_insured')}
                          </td>
                          {hasSpouse && (
                            <td className="p-2 border">
                              {renderQuestion(question, 'spouse')}
                            </td>
                          )}
                          {[...Array(numberOfChildren)].map((_, i) => (
                            <td key={i} className="p-2 border">
                              {renderQuestion(question, `child_${i + 1}`)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bloc assurance antérieure (par personne, en dessous du tableau) */}
              {/* <div className="mt-6 space-y-4">
                <h3 className="font-bold text-primary">Assurance antérieure</h3>
                {persons.map((person) => {
                  const info = person.childIndex !== null
                    ? (previousInsurance.children[person.childIndex] || { hadInsurance: null, cardPhoto: null })
                    : previousInsurance[person.prevKey];

                  return (
                    <div key={person.key} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="font-medium text-blue-800 mb-2">
                        {person.label} — Avez-vous déjà été assuré(e) auparavant ?
                      </p>
                      <div className="flex gap-4 mb-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`hadInsurance_${person.childIndex !== null ? `child_${person.childIndex}` : person.prevKey}`}
                            value="yes"
                            checked={info?.hadInsurance === 'yes'}
                            onChange={() => handleHadInsurance(person.prevKey, 'yes', person.childIndex)}
                          />
                          <span>Oui</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`hadInsurance_${person.childIndex !== null ? `child_${person.childIndex}` : person.prevKey}`}
                            value="no"
                            checked={info?.hadInsurance === 'no'}
                            onChange={() => handleHadInsurance(person.prevKey, 'no', person.childIndex)}
                          />
                          <span>Non</span>
                        </label>
                      </div>
                      {info?.hadInsurance === 'yes' && (
                        <div>
                          <label className="block text-sm font-medium mb-1 text-blue-800">
                            Photo de la carte d'assurance
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleInsuranceCardPhoto(person.prevKey, e.target.files[0], person.childIndex)}
                            className="w-full border-2 border-blue-300 rounded-xl px-3 py-2 text-sm"
                          />
                          {info?.cardPhoto && (
                            <img src={info.cardPhoto} alt="Carte d'assurance" className="mt-2 h-20 object-contain rounded" />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div> */}

              {/* Boutons de navigation */}
              <div className="text-center mt-8 flex justify-center gap-4">
                <Link
                  to="/step-5"
                  className="rounded-full border border-gray-300 px-6 py-3 text-gray-700 flex items-center gap-2 hover:bg-gray-50"
                >
                  ← Retour
                </Link>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`rounded-full border border-gray-300 px-6 py-3 flex items-center gap-2 bg-primary text-secondary hover:bg-primary-dark ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isLoading ? 'Calcul en cours...' : 'Valider →'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
